import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * Cart Drawer Recommendations
 *
 * A custom element that manages a product recommendation widget
 * inside the cart drawer. It hides products that are already in the cart
 * and provides "add to cart" functionality.
 */
class CartDrawerRecommendations extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Listen for cart updates to refresh which products are visible
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });

    // Listen for clicks on add buttons
    this.addEventListener('click', this.#handleClick, { signal });

    // Initial sync — fetch current cart to ensure correct state
    this.#syncWithCart();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Handles the cart update event.
   * Hides products that are now in the cart, shows products that were removed.
   *
   * @param {Event} event
   */
  #handleCartUpdate = async (event) => {
    const cart = /** @type {any} */ (event).detail?.resource;

    if (cart?.items) {
      this.#updateVisibility(cart.items);
    } else {
      // No cart data in event — fetch it
      await this.#syncWithCart();
    }
  };

  /**
   * Fetches the current cart and updates product visibility.
   */
  async #syncWithCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.#updateVisibility(cart.items || []);
    } catch (e) {
      // Silently fail — products stay visible
    }
  }

  /**
   * Updates the visibility of recommendation items based on cart contents.
   *
   * @param {Array<{product_id: number, variant_id: number}>} cartItems
   */
  #updateVisibility(cartItems) {
    const cartProductIds = new Set(cartItems.map((item) => item.product_id.toString()));

    const items = this.querySelectorAll('.cart-drawer-reco__item');
    items.forEach((item) => {
      const productId = item.getAttribute('data-product-id');
      if (!productId) return;

      if (cartProductIds.has(productId)) {
        item.classList.add('cart-drawer-reco__item--hidden');
      } else {
        item.classList.remove('cart-drawer-reco__item--hidden');
      }
    });
  }

  /**
   * Handles click events — delegates to add-to-cart buttons.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target)?.closest('.cart-drawer-reco__add-btn');
    if (!button || button.disabled) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    this.#addToCart(variantId, button);
  };

  /**
   * Adds a product variant to the cart.
   *
   * @param {string} variantId
   * @param {HTMLButtonElement} button
   */
  async #addToCart(variantId, button) {
    // Disable button to prevent double-clicks
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = '...';

    try {
      // Get the section IDs so the cart drawer content updates
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: sectionIds.join(','),
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status) {
        // Error adding — re-enable
        button.textContent = originalText;
        button.disabled = false;
        return;
      }

      // Immediately hide this recommended item
      const recoItem = button.closest('.cart-drawer-reco__item');
      if (recoItem) {
        recoItem.classList.add('cart-drawer-reco__item--hidden');
      }

      // Fetch the full cart to get item_count and dispatch a proper CartAddEvent
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Dispatch CartAddEvent so cart-drawer and cart-items-component update
      document.dispatchEvent(
        new CartAddEvent(cart, 'cart-drawer-recommendations', {
          source: 'cart-drawer-recommendations',
          itemCount: cart.item_count,
          variantId: variantId,
          sections: data.sections,
        })
      );
    } catch (e) {
      // Re-enable button on network error
      button.textContent = originalText;
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
